import React from 'react';
import {
  ECO_SOUND_META,
  ECO_SOUND_PATHS,
  type EcoRitmoSessionConfig,
  type EcoSoundId,
  type TimeSignature,
} from '../../lib/rhythm/ecoRitmoConfig';
import {
  micInputLabel,
  type EcoMicInputMode,
  type EcoMicSettings,
} from '../../lib/rhythm/ecoMicConfig';
import { playPercusionSound } from '../../lib/usePercusionSounds';

interface EcoGlobalSettingsProps {
  config: EcoRitmoSessionConfig;
  micSettings: EcoMicSettings;
  onTimeSignature: (ts: TimeSignature) => void;
  onBpm: (bpm: number) => void;
  onSound: (soundId: EcoSoundId) => void;
  onMicSettings: (settings: EcoMicSettings) => void;
  canPlay: boolean;
  onPlay: () => void;
}

const TIME_SIGNATURES: TimeSignature[] = ['2/4', '3/4', '4/4'];

const SOUNDS: EcoSoundId[] = ['clap', 'snare', 'shaker'];
const MIC_MODES: EcoMicInputMode[] = ['tap', 'mic', 'both'];

export const EcoGlobalSettings: React.FC<EcoGlobalSettingsProps> = ({
  config,
  micSettings,
  onTimeSignature,
  onBpm,
  onSound,
  onMicSettings,
  canPlay,
  onPlay,
}) => {
  const handleSound = (id: EcoSoundId) => {
    onSound(id);
    playPercusionSound(ECO_SOUND_PATHS[id]);
  };

  const micUsesMic = micSettings.inputMode !== 'tap';

  return (
    <footer className="eco-settings barra-inferior shrink-0 flex flex-col gap-2">
      <div className="flex items-stretch gap-2 sm:gap-3">
        <div className="eco-settings-grid flex-1 min-w-0 grid grid-cols-3 gap-2 sm:gap-3 items-center">
        <div className="flex flex-col gap-1">
          <span className="eco-settings-label">Compás</span>
          <div className="flex gap-1">
            {TIME_SIGNATURES.map((ts) => {
              const sel = config.timeSignature === ts;
              return (
                <button
                  key={ts}
                  type="button"
                  onClick={() => onTimeSignature(ts)}
                  className={`eco-chip flex-1 py-1.5 ${sel ? 'eco-chip--active' : ''}`}
                >
                  {ts}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <span className="eco-settings-label">Tempo</span>
            <span className="eco-chip-count">{config.bpm}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm leading-none opacity-60" title="Lento" aria-hidden>
              🐢
            </span>
            <input
              type="range"
              min={50}
              max={120}
              step={2}
              value={config.bpm}
              onChange={(e) => onBpm(Number(e.target.value))}
              className="eco-tempo-slider flex-1 min-w-0"
              aria-label="Tempo"
            />
            <span className="text-sm leading-none opacity-60" title="Rápido" aria-hidden>
              🐇
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="eco-settings-label">Percusión</span>
          <div className="flex gap-1">
            {SOUNDS.map((id) => {
              const meta = ECO_SOUND_META[id];
              const sel = config.soundId === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSound(id)}
                  className={`eco-chip flex-1 py-1 text-lg leading-none ${sel ? 'eco-chip--active' : ''}`}
                  title={meta.label}
                  aria-label={meta.label}
                >
                  {meta.emoji}
                </button>
              );
            })}
          </div>
        </div>
        </div>

        <button
          type="button"
          onClick={() => canPlay && onPlay()}
          disabled={!canPlay}
          className="eco-play-bottom shrink-0 flex flex-col items-center justify-center gap-0.5 disabled:opacity-35 disabled:pointer-events-none transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          aria-label="Play"
        >
          <span className="eco-play-bottom-icon flex items-center justify-center text-xl pl-0.5">
            ▶
          </span>
          <span className="text-[11px] font-semibold tracking-wide">Play</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-0.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="eco-settings-label shrink-0">Entrada</span>
          <div className="flex gap-1">
            {MIC_MODES.map((mode) => {
              const sel = micSettings.inputMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onMicSettings({ ...micSettings, inputMode: mode })}
                  className={`eco-chip px-2 py-1 text-[10px] sm:text-xs ${sel ? 'eco-chip--active' : ''}`}
                >
                  {micInputLabel(mode)}
                </button>
              );
            })}
          </div>
        </div>

        {micUsesMic && (
          <div className="flex items-center gap-2 flex-1 min-w-[140px] max-w-xs">
            <span className="eco-settings-label shrink-0">Sensibilidad</span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={micSettings.sensitivity}
              onChange={(e) =>
                onMicSettings({ ...micSettings, sensitivity: Number(e.target.value) })
              }
              className="eco-tempo-slider flex-1 min-w-0"
              aria-label="Sensibilidad del micrófono"
            />
            <span className="eco-chip-count w-6 text-center">{micSettings.sensitivity}</span>
          </div>
        )}
      </div>
    </footer>
  );
};

export default EcoGlobalSettings;
